import React from "react"
import { ExternalLink } from "lucide-react"
import { GalleryItem } from "../types/objects"
import { SectionHeading } from "./ui/SectionHeading"

/**
 * Props interface for Gallery component
 */
interface GalleryProps {
	data?: GalleryItem[]
}

/** Strips protocol and trailing slash for a cleaner link label */
const prettyUrl = (url: string) => url.replace(/^https?:\/\//, "").replace(/\/$/, "")

/**
 * Gallery Component — reference projects: intro text and a card per project (Buldo first).
 */
export const Gallery: React.FC<GalleryProps> = ({ data }) => {
	/**
	 * Sorting Logic: Ensures "Buldo" item always appears first in the list.
	 */
	const sortedItems = React.useMemo(() => {
		if (!data) return null
		return [...data].sort((a, b) => {
			const titleA = (a.title || "").toLowerCase()
			const titleB = (b.title || "").toLowerCase()
			if (titleA === "buldo" && titleB !== "buldo") return -1
			if (titleB === "buldo" && titleA !== "buldo") return 1
			return 0
		})
	}, [data])

	return (
		<section id="portfolio" className="lp-section">
			<div className="lp-container">
				<SectionHeading
					eyebrow="Reference projekty"
					title="Galerie"
					lead="Ukázky spolupráce s technologickými a výrobními firmami, pro které propojujeme systémy a automatizujeme procesy."
				/>

				{/* Introduction Text Block */}
				<div className="lp-portfolio__intro">
					<p data-reveal="">
						U technologické firmy <strong>Buldo</strong> jsme navrhli
						standardizované řešení pro efektivní propojení jejich interních
						systémů s aplikacemi třetích stran. Spolupráce zahrnovala
						specifikaci integračních standardů, návrh API a zajištění souladu
						s dlouhodobou architekturou řešení.
					</p>
					<p data-reveal="" style={{ "--reveal-delay": "80ms" } as React.CSSProperties}>
						Spolupracujeme s výrobními firmami a technologickými partnery na
						zefektivnění jejich klíčových i podpůrných procesů. Každé spolupráci
						předchází důkladná analýza potřeb a možností integrace moderních
						nástrojů do stávajícího prostředí klienta.
					</p>
					<p data-reveal="" style={{ "--reveal-delay": "160ms" } as React.CSSProperties}>
						Pro platformu <strong>Intedat</strong>, zaměřenou na B2B vyhledávání
						a párování obchodních partnerů, zajišťujeme podporu v oblasti
						integrací na systémy třetích stran, jako jsou například CRM Raynet
						nebo Pipedrive. Naším cílem je umožnit platformě lépe pracovat s
						obchodními daty, zautomatizovat klíčové procesy a zefektivnit práci
						uživatelů.
					</p>
					<p data-reveal="" style={{ "--reveal-delay": "240ms" } as React.CSSProperties}>
						Pro efektivní propojení našich systémů jsme využili vývoj <strong>adaptérů
						na platformě Make</strong>, které zajistily bezproblémové napojení na reálná
						datová rozhraní (např. Email). Výsledkem je plně automatizovaný tok dat mezi
						webem a interní databází projektů a produktů. To umožňuje firmě prezentovat
						své portfolio vždy v naprosto aktuální podobě a bez jakýchkoliv ručních zásahů.
					</p>
				</div>

				{/* Project Gallery Cards */}
				<div className="lp-grid lp-grid--3">
					{sortedItems ? (
						sortedItems.map((item, idx) => {
							const href = item.web?.startsWith("http") ? item.web : `https://${item.web}`
							return (
								<article
									key={`${item.title}-${idx}`}
									className="lp-card lp-card--bar lp-project"
									data-reveal=""
									style={{ "--reveal-delay": `${idx * 100}ms` } as React.CSSProperties}
								>
									<span className="lp-project__mark" aria-hidden="true">
										{item.title.charAt(0).toUpperCase()}
									</span>
									<h3 className="lp-card__title">{item.title}</h3>
									<span className="lp-project__label">Více informací najdete na:</span>
									<a
										href={href}
										target="_blank"
										rel="noopener noreferrer"
										className="lp-project__link"
									>
										{prettyUrl(item.web)} <ExternalLink size={16} />
									</a>
								</article>
							)
						})
					) : (
						<p>Načítání odkazovaných webů…</p>
					)}
				</div>
			</div>
		</section>
	)
}
